import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, errorText } from "@/lib/api";
import { n2, shortDate, STATS, STAT_LABEL } from "@/lib/format";
import { TeamTotalsAccuracy } from "@/components/accuracy/TeamTotalsAccuracy";
import { LineupSourceAccuracy } from "@/components/accuracy/LineupSourceAccuracy";
import { MinutesGroupReport } from "@/components/accuracy/MinutesGroupReport";
import { ScoreModelAccuracy } from "@/components/accuracy/ScoreModelAccuracy";
import { CalibrationAccuracy } from "@/components/accuracy/CalibrationAccuracy";
import "@/components/accuracy/AccuracyArena.css";

const MODES = [
  { id: "saved", label: "Saved morning projections" },
  { id: "backtest", label: "Backtest (last 8 game weeks)" },
];

function SummaryCard({ stat, s }) {
  const better = s.model_mae < s.baseline_mae;
  const gain = ((1 - s.model_mae / s.baseline_mae) * 100).toFixed(1);
  return (
    <div data-testid={`acc-card-${stat}`} className="aa-card">
      <span>{STAT_LABEL[stat]} MAE</span>
      <strong>{n2(s.model_mae)}</strong>
      <small>Season avg {n2(s.baseline_mae)}</small>
      <em className={better ? "positive" : "negative"}>{better ? "▲" : "▼"} {gain}% vs baseline · n={s.n}</em>
    </div>
  );
}

function PlayerAccuracy({ data, mode }) {
  const chart = STATS.filter((s) => data.summary[s]).map((s) => ({ stat: STAT_LABEL[s], Model: data.summary[s].model_mae, "Season avg": data.summary[s].baseline_mae }));
  return (
    <section className="aa-section">
      <h2>Player stats vs actual</h2>
      <p className="aa-explainer">Per-player projection error by stat ({mode === "saved" ? "SNAPSHOT" : "BACKTEST"}), compared with a plain season-average baseline.</p>
      <div className="aa-cards">{STATS.filter((s) => data.summary[s]).map((s) => <SummaryCard key={s} stat={s} s={data.summary[s]} />)}</div>
      <div className="aa-chart" data-testid="acc-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart} margin={{ left: -18 }}>
            <CartesianGrid stroke="#1a3554" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="stat" tick={{ fill: "#8fb0d2", fontSize: 12 }} />
            <YAxis tick={{ fill: "#6b87aa", fontSize: 11 }} />
            <Tooltip contentStyle={{ background: "#071323", border: "1px solid #168aff", borderRadius: 8 }} cursor={{ fill: "rgba(22,138,255,0.08)" }} />
            <Legend />
            <Bar dataKey="Model" fill="#168aff" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Season avg" fill="#33496a" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="aa-table-scroll">
        <table className="aa-table" data-testid="acc-weekly-table">
          <thead><tr><th>Week of</th>{STATS.map((s) => <th key={s} className="r">{STAT_LABEL[s]} model / avg</th>)}</tr></thead>
          <tbody>
            {data.weeks.map((w) => (
              <tr key={w.week}>
                <td className="aa-date">{shortDate(w.week)}</td>
                {STATS.map((s) => {
                  const v = w.stats[s];
                  return <td key={s} className="r aa-number">{v ? <><span className={v.model_mae <= v.baseline_mae ? "aa-good" : "aa-bad"}>{n2(v.model_mae)}</span><span className="aa-vs"> / {n2(v.baseline_mae)}</span></> : "–"}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function Accuracy() {
  const [mode, setMode] = useState("saved");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [totals, setTotals] = useState(null);

  useEffect(() => { api.get("/rotation/accuracy", { params: { limit: 300 } }).then((r) => setTotals(r.data)).catch(() => setTotals(false)); }, []);
  useEffect(() => {
    setData(null); setError("");
    api.get("/accuracy", { params: { mode } }).then((r) => setData(r.data)).catch((e) => setError(errorText(e)));
  }, [mode]);

  const empty = data && !data.weeks.length;
  return (
    <section className="aa-page" data-testid="accuracy-page">
      <header className="aa-hero">
        <div className="aa-hero-copy">
          <p className="aa-eyebrow">Model accuracy</p>
          <h1>Mean absolute <em>error</em></h1>
          <p className="aa-note" data-testid={empty ? "acc-empty" : "acc-note"}>
            {empty ? (mode === "saved" ? "No finished games with a saved morning projection yet. They appear the day after the first daily update." : "Not enough stored games yet. Run the backfill in Settings.")
              : "Compare forecasts with actual NBA results. Backtests are not saved pregame predictions."}
          </p>
        </div>
        <div className="aa-tabs" role="group" aria-label="Accuracy data source">
          {MODES.map((m) => (
            <button key={m.id} type="button" data-testid={`acc-mode-${m.id}`} aria-pressed={mode === m.id} className={mode === m.id ? "active" : ""} onClick={() => setMode(m.id)}>{m.label}</button>
          ))}
        </div>
      </header>
      <div className="aa-body">
        <TeamTotalsAccuracy data={totals} mode={mode} />
        <ScoreModelAccuracy />
        <CalibrationAccuracy />
        {error && <p className="aa-bad">{error}</p>}
        {!data && !error && <p className="aa-empty" data-testid="acc-loading">Calculating…</p>}
        {data && data.weeks.length > 0 && <PlayerAccuracy data={data} mode={mode} />}
        <LineupSourceAccuracy />
        <MinutesGroupReport />
      </div>
    </section>
  );
}
